/**
 * Jusqu'où va le relevé bancaire importé.
 *
 * La grille prévisionnelle couvre douze mois ; le relevé, lui, s'arrête au
 * dernier import. Sans le savoir, la grille comparait le prévu d'octobre à un
 * réel d'octobre… qui n'existe pas encore : 0 €, affiché comme une économie.
 * Constaté le 06/10/2026 : « ≈ 0 » sous octobre, novembre et décembre, et un
 * total annuel qui opposait 12 mois de prévu à 9 mois de relevé.
 *
 * Pour chaque mois, cette fonction dit ce qu'on sait :
 *
 *  - `complet` : le relevé couvre tout le mois, l'écart avec le prévu a un sens ;
 *  - `partiel` : le relevé s'arrête en cours de mois — on montre ce qui est
 *    passé, sans en tirer d'écart ;
 *  - `aucun`   : pas de relevé pour ce mois, ni avant le premier import ni après
 *    le dernier. Un réel à 0 y serait un mensonge.
 */

export type CouvertureMois = 'complet' | 'partiel' | 'aucun';

export interface CouvertureReleve {
  /** Le dernier jour présent dans le relevé de l'année (`AAAA-MM-JJ`), ou `null`. */
  dernierJour: string | null;
  /** Douze valeurs, index 0 = janvier. */
  parMois: CouvertureMois[];
  /** Combien de mois sont `complet` : la base de toute comparaison annuelle. */
  moisComplets: number;
}

function moisDe(jour: string): number {
  return Number(jour.slice(5, 7)) - 1;
}

/** Le dernier jour calendaire du mois de `jour`. */
function estFinDeMois(jour: string): boolean {
  const a = Number(jour.slice(0, 4));
  const m = Number(jour.slice(5, 7));
  const j = Number(jour.slice(8, 10));
  // Le jour 0 du mois suivant est le dernier jour du mois courant.
  return new Date(Date.UTC(a, m, 0)).getUTCDate() === j;
}

/**
 * @param jours  les dates des mouvements (`AAAA-MM-JJ`), dans n'importe quel ordre
 * @param annee  l'année affichée : les jours d'une autre année sont ignorés
 */
export function couvertureReleve(jours: readonly string[], annee: number): CouvertureReleve {
  const prefixe = `${String(annee)}-`;
  const dansLAnnee = jours.filter((j) => /^\d{4}-\d{2}-\d{2}/.test(j) && j.startsWith(prefixe));

  if (dansLAnnee.length === 0) {
    return {
      dernierJour: null,
      parMois: Array.from({ length: 12 }, (): CouvertureMois => 'aucun'),
      moisComplets: 0,
    };
  }

  let premier = dansLAnnee[0] ?? '';
  let dernier = premier;
  for (const j of dansLAnnee) {
    if (j < premier) premier = j;
    if (j > dernier) dernier = j;
  }
  const moisPremier = moisDe(premier);
  const moisDernier = moisDe(dernier);
  // Un relevé qui s'arrête le 30 septembre couvre septembre ; le 12, non.
  const dernierMoisComplet = estFinDeMois(dernier);

  const parMois = Array.from({ length: 12 }, (_, i): CouvertureMois => {
    // Avant le premier import : le CRM ne savait encore rien de ce compte.
    if (i < moisPremier || i > moisDernier) return 'aucun';
    if (i === moisDernier) return dernierMoisComplet ? 'complet' : 'partiel';
    return 'complet';
  });

  return {
    dernierJour: dernier.slice(0, 10),
    parMois,
    moisComplets: parMois.filter((c) => c === 'complet').length,
  };
}
